import { Graphics } from "pixi.js"

// The ground marker that says "a real person is driving this body".
//
// Other players are LPC characters exactly like every NPC in the world, so nothing about the art
// tells one apart -- and the cues that would (a permanent name plate, a chat bubble) either cost
// vertical space above the head or only appear when the player happens to speak. This is the quietest
// cue that still reads at a glance: a thin cool-toned ellipse painted on the ground under the feet,
// behind the body, so the character stands inside it. It occupies no space above the head, never
// occludes a neighbour, and a dozen of them on screen still parse -- which a dozen name plates do not.
//
// Deliberately NOT an outline glow: that vocabulary belongs to the hover/interaction highlight (see
// entity_highlight), and a permanent rim would read as "you can act on this" all the time. It is not
// animated either -- no ticker, no pulse. This is a standing fact about the entity, not an alert, and
// a static Graphics costs nothing on the low-end machines this game targets.

// Geometry lives in the sprite's own 64x64 art space; the sprite anchors the ring on the same foot
// line it draws its shadow on, so the ring sits where the character's weight is (and rides up with it
// when the body is submerged).
const RING_RX = 13.5
const RING_RY = 6.25
const RING_WIDTH = 1.5
// A darker contour under the bright ring, a pixel wider on each side, so the cue survives pale ground
// (sand, snow, the camp's trodden dirt) as well as dark grass -- the same trick the name plate plays
// with its black text stroke.
const CONTOUR_COLOR = 0x0b1a26
const CONTOUR_WIDTH = RING_WIDTH + 2
// Alphas of the contour and the outer bloom, both expressed as a fraction of the ring's own alpha, so
// tuning `alpha` fades the whole marker together instead of pulling it apart.
const CONTOUR_ALPHA_SHARE = 0.4
// A very faint filled ellipse just outside the ring: it beds the marker into the ground instead of
// leaving it looking like a decal laid on top.
const GLOW_RX = RING_RX + 2
const GLOW_RY = RING_RY + 1.5
const GLOW_ALPHA_SHARE = 0.14

export interface PlayerRingTuning {
    enabled: boolean
    // Cool sky blue: clear of the hover glow's amber, of the warm ember palette, and of the red/amber/
    // green an HP cue would use, so the ring can only mean one thing.
    color: number
    alpha: number
    // Multiplier on every radius, for trying a tighter or wider footprint without a rebuild.
    scale: number
}

export const DEFAULT_PLAYER_RING_TUNING: PlayerRingTuning = {
    enabled: true,
    color: 0x8fd6ff,
    alpha: 0.8,
    scale: 1,
}

let tuning: PlayerRingTuning = { ...DEFAULT_PLAYER_RING_TUNING }

export const playerRingTuning = (): PlayerRingTuning => tuning

// Every ring currently on screen. Tuning from the console repaints what is already there rather than
// only reaching players who walk into view afterwards; rings drop out of the set in dispose().
const liveRings = new Set<PlayerRing>()

export const tunePlayerRing = (next: Partial<PlayerRingTuning> | boolean): PlayerRingTuning => {
    const patch = typeof next === "boolean" ? { enabled: next } : next || {}
    tuning = { ...tuning, ...patch }
    liveRings.forEach(ring => ring.redraw())
    return tuning
}

// Reset to the shipped look. Exists for tests, which share module state with each other.
export const resetPlayerRingTuning = (): PlayerRingTuning => tunePlayerRing({ ...DEFAULT_PLAYER_RING_TUNING })

export class PlayerRing extends Graphics {
    constructor() {
        super()
        liveRings.add(this)
        this.redraw()
    }

    redraw(): void {
        this.clear()

        const { enabled, color, alpha, scale } = tuning
        this.visible = enabled
        if (!enabled) {
            return
        }

        const rx = RING_RX * scale
        const ry = RING_RY * scale

        // Bloom first, then the dark contour, then the bright ring on top of it.
        this.beginFill(color, alpha * GLOW_ALPHA_SHARE)
        this.drawEllipse(0, 0, GLOW_RX * scale, GLOW_RY * scale)
        this.endFill()

        this.lineStyle(CONTOUR_WIDTH, CONTOUR_COLOR, alpha * CONTOUR_ALPHA_SHARE)
        this.drawEllipse(0, 0, rx, ry)

        this.lineStyle(RING_WIDTH, color, alpha)
        this.drawEllipse(0, 0, rx, ry)
        this.lineStyle(0)
    }

    // Decoration must never resize the thing it decorates. A renderable derives its on-screen y from
    // its own measured height (BaseRenderable.getYOffset reads `this.height`, a bounds query over every
    // child), so a ring that widened those bounds would nudge the very character it marks. Reporting no
    // bounds keeps the marker out of every measurement taken of the entity -- the same reasoning as the
    // hover outline's BoundlessContainer.
    calculateBounds(): void {
        this._bounds.clear()
    }

    // Stop tracking this ring for live tuning. The owning sprite calls this from its cleanup, which
    // runs on a paperdoll rebuild as well as on teardown -- remove() only detaches, it never destroys,
    // so without this the set would grow by one entry per rebuild.
    dispose(): void {
        liveRings.delete(this)
    }

    destroy(options?: any): void {
        this.dispose()
        super.destroy(options)
    }
}

// Browser-console tuning, in the same shape as window.spriteShadow / window.deathEffect:
//
//     playerRing = false                 // off
//     playerRing = { scale: 1.4 }        // wider footprint
//     playerRing = { color: 0xff6688 }   // try another hue
//     playerRing                         // read the active values back
//
// Guarded so it is a no-op in the Node test harness.
if (typeof window !== "undefined") {
    Object.defineProperty(window, "playerRing", {
        get: () => tuning,
        set: (next: Partial<PlayerRingTuning> | boolean) => {
            console.log("[playerRing]", tunePlayerRing(next))
        },
        configurable: true,
    })
}
